import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { HeatmapCell } from '../tracking-demo.models';

/**
 * Dependency-free click heatmap. Renders weighted cells ([x‰, y‰, weight], normalized to
 * per-mille of the recorded viewport) as additive radial blobs on a canvas — hotter where
 * clicks stack. Presentation only: the caller supplies the points.
 *
 * Two modes: standalone (its own viewport-shaped dark box, sized by `aspect`) or
 * `overlay` — a transparent, click-through layer filling the nearest positioned ancestor
 * (the paused replay frame it sits on). Redraws on input change and on resize.
 */
@Component({
  selector: 'app-heatmap-canvas',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div #host
         [class]="overlay ? 'pointer-events-none absolute inset-0' : 'relative w-full overflow-hidden rounded-lg bg-slate-900/90'"
         [style.aspectRatio]="overlay ? null : aspect">
      <canvas #cv class="absolute inset-0 h-full w-full"></canvas>
      @if (!overlay && !points.length) {
        <div class="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
          No click data yet
        </div>
      }
    </div>
  `,
})
export class HeatmapCanvasComponent implements AfterViewInit, OnDestroy {
  /** Aspect ratio (w / h) of the surface being heatmapped (standalone mode). */
  @Input() aspect = 16 / 9;
  /** Transparent layer over a background the parent provides. */
  @Input() overlay = false;

  private _points: HeatmapCell[] = [];
  @Input() set points(value: HeatmapCell[] | null | undefined) {
    this._points = Array.isArray(value) ? value : [];
    this.draw();
  }
  get points(): HeatmapCell[] {
    return this._points;
  }

  private readonly host = viewChild.required<ElementRef<HTMLDivElement>>('host');
  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('cv');
  private ready = false;
  private observer: ResizeObserver | null = null;

  /** Blob radius as a fraction of the smaller canvas dimension. */
  private static readonly RADIUS_FRAC = 0.06;

  ngAfterViewInit(): void {
    this.ready = true;
    this.observer = new ResizeObserver(() => this.draw());
    this.observer.observe(this.host().nativeElement);
    this.draw();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private draw(): void {
    if (!this.ready) return;
    const el = this.canvas().nativeElement;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (w <= 0 || h <= 0) return;
    el.width = w; // match the backing store to the CSS size so blobs aren't stretched
    el.height = h;
    const ctx = el.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, w, h);
    if (!this._points.length) return;

    const maxWeight = Math.max(...this._points.map((p) => p[2] || 1), 1);
    const radius = Math.max(8, Math.min(w, h) * HeatmapCanvasComponent.RADIUS_FRAC);
    ctx.globalCompositeOperation = 'lighter'; // overlapping clicks build intensity
    for (const [xPerMille, yPerMille, weight] of this._points) {
      const x = (xPerMille / 1000) * w;
      const y = (yPerMille / 1000) * h;
      const alpha = 0.15 + 0.55 * ((weight || 1) / maxWeight);
      const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
      g.addColorStop(0, `rgba(239, 68, 68, ${alpha})`);
      g.addColorStop(0.5, `rgba(249, 115, 22, ${alpha * 0.5})`);
      g.addColorStop(1, 'rgba(249, 115, 22, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }
}
